import { useCallback, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { ContentTypeAppLabels } from '@webapp/core/entities/internal/enums/ContentTypeAppLabelEnum'
import { ContentTypeModels } from '@webapp/core/entities/internal/enums/ContentTypeModelEnum'
import { makeSelectContentTypesByModel } from '@webapp/core/stores/global'
import { useAppRoutes } from '@webapp/hooks'
import { type CreditCardItem } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { type ReconcileMatch } from '@webapp/modules/accounts-payable/entities/ReconcileMatch'
import { type ReconcileSession } from '@webapp/modules/accounts-payable/entities/ReconcileSession'
import { type StatementItem } from '@webapp/modules/accounts-payable/entities/StatementItem'
import { useArchiveCreditCardItemPresenter } from '@webapp/modules/accounts-payable/features/archive-credit-card-item/presenters/ArchiveCreditCardItemPresenter'
import { useCreateMatchPresenter } from '@webapp/modules/accounts-payable/features/create-match/presenters/CreateMatchPresenter'
import { useDuplicateStatementItemPresenter } from '@webapp/modules/accounts-payable/features/duplicate-statement-item/presenters/DuplicateStatementItemPresenter'
import { useCreditCardStatementDetailsPresenter } from '@webapp/modules/accounts-payable/features/get-credit-card-account/presenters/CreditCardStatementDetailsPresenter'
import { useMatchedTransactionListPresenter } from '@webapp/modules/accounts-payable/features/list-matched-transactions/presenters/MatchedTransactionListPresenter'
import { useUnmatchedCreditCardItemListPresenter } from '@webapp/modules/accounts-payable/features/list-unmatched-credit-card-items/presenters/UnmatchedCreditCardItemListPresenter'
import { useUnmatchedStatementItemListPresenter } from '@webapp/modules/accounts-payable/features/list-unmatched-statement-items/presenters/UnmatchedStatementItemListPresenter'
import { useRemoveStatementItemPresenter } from '@webapp/modules/accounts-payable/features/remove-statement-item/presenters/RemoveStatementItemPresenter'
import { useUnmatchMatchPresenter } from '@webapp/modules/accounts-payable/features/unmatch-match/presenters/UnmatchMatchPresenter'
import {
  MatchPageTabs,
  type MatchPageTabEnum,
} from '@webapp/modules/accounts-payable/pages/types'

export interface ItemModalState {
  mode: 'create' | 'edit' | 'view'
  item?: CreditCardItem
}

export interface IUseReconciliationMatchViewParams {
  accountId: number
  tab: MatchPageTabEnum
}

const roundToCents = (value: number) => Math.round(value * 100) / 100

// The reconcile app registers its own creditcarditem content type —
// app-label-qualified enum value split into selector args (BHV-043).
const RECONCILE_ITEM_MODEL =
  ContentTypeModels.RECONCILE_CREDITCARDITEM.split('/')[1]

export const useReconciliationMatchView = ({
  accountId,
  tab,
}: IUseReconciliationMatchViewParams) => {
  const history = useHistory()
  const { url } = useAppRoutes()

  // Presenter companion hook — never the Model hook (ADR-2); React Query
  // dedupes via the shared `creditCardAccountQueries.detail` config
  // (Sibling Pages pattern).
  const { account, isLoading: isLoadingAccount } =
    useCreditCardStatementDetailsPresenter({ accountId })

  // Latest session (immediate variance recompute, BHV-035/036) — the account
  // refetch triggered by edit-reconcile-session catches up asynchronously.
  const [sessionOverride, setSessionOverride] =
    useState<Nullable<ReconcileSession>>(null)
  const session: Nullable<ReconcileSession> =
    sessionOverride ?? account?.session ?? null

  // Audit-log content type resolved at runtime — never hardcoded (BHV-043).
  const reconcileItemContentType = useSelector(
    makeSelectContentTypesByModel(
      ContentTypeAppLabels.RECONCILE,
      RECONCILE_ITEM_MODEL
    )
  )

  // Cross-feature selection state (ADR-9) — variance needs BOTH panes.
  const [selectedCcItems, setSelectedCcItems] = useState<CreditCardItem[]>([])
  const [selectedStatementItems, setSelectedStatementItems] = useState<
    StatementItem[]
  >([])
  const [filterByCcItemId, setFilterByCcItemId] =
    useState<Nullable<number>>(null)
  const [justCreatedItemIds, setJustCreatedItemIds] = useState<number[]>([])

  // FS-owned modal lifecycles.
  const [itemModal, setItemModal] = useState<Nullable<ItemModalState>>(null)
  const [statementItemModalOpen, setStatementItemModalOpen] = useState(false)
  const [unmatchModal, setUnmatchModal] =
    useState<Nullable<ReconcileMatch>>(null)
  const [auditLogItem, setAuditLogItem] =
    useState<Nullable<CreditCardItem>>(null)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [matchConfirmOpen, setMatchConfirmOpen] = useState(false)

  // Tab badges from the features' presenter companion hooks — React Query
  // dedupes with the rendered presenters (BHV-030).
  const {
    data: { unmatchedCount },
  } = useUnmatchedCreditCardItemListPresenter({ accountId })
  const {
    data: { unmatchedCount: statementItemCount },
  } = useUnmatchedStatementItemListPresenter({ accountId, filterByCcItemId })
  const {
    data: { confirmedMatches, clearedBalance, matchedCount },
  } = useMatchedTransactionListPresenter({ sessionId: session?.id ?? null })

  const { commands: matchCommands, states: matchStates } =
    useCreateMatchPresenter()
  const { commands: unmatchCommands, states: unmatchStates } =
    useUnmatchMatchPresenter()
  const { commands: duplicateCommands } = useDuplicateStatementItemPresenter()
  const { commands: removeCommands } = useRemoveStatementItemPresenter()
  const { commands: archiveCommands } = useArchiveCreditCardItemPresenter()

  // FS-owned cross-pane math (ADR-9, BHV-031/032/036).
  const selectionTotal = useMemo(
    () => selectedCcItems.reduce((sum, item) => sum + item.totalCost, 0),
    [selectedCcItems]
  )
  const statementSelectionTotal = useMemo(
    () =>
      selectedStatementItems.reduce((sum, item) => sum + item.amountValue, 0),
    [selectedStatementItems]
  )
  const selectionVariance = roundToCents(
    selectionTotal - statementSelectionTotal
  )
  const hasInvalidCurrency = account
    ? selectedCcItems.some((item) =>
        item.hasInvalidCurrency(account.currency.id)
      )
    : false
  const hasSelection =
    selectedCcItems.length > 0 || selectedStatementItems.length > 0
  const isMatchDisabled =
    selectedCcItems.length === 0 ||
    hasInvalidCurrency ||
    (selectedStatementItems.length > 0 && selectionVariance !== 0)

  const endingBalance = session?.balanceValue ?? 0
  const overallVariance = roundToCents(endingBalance - (clearedBalance ?? 0))
  const isSubmitDisabled =
    overallVariance !== 0 || confirmedMatches.length === 0

  const clearSelections = useCallback(() => {
    setSelectedCcItems([])
    setSelectedStatementItems([])
  }, [])

  const performMatch = useCallback(async () => {
    if (!session) return

    try {
      await matchCommands.match({
        sessionId: session.id,
        creditcardItemIds: selectedCcItems.map((item) => item.id),
        statementItemIds: selectedStatementItems.map((item) => item.id),
      })
      // Success clears both selections; the feature's invalidations refetch
      // all three datasets (BHV-034).
      clearSelections()
    } catch {
      // Failure keeps the selection — the feature already toasted (ADR-10).
    }
  }, [
    clearSelections,
    matchCommands,
    selectedCcItems,
    selectedStatementItems,
    session,
  ])

  // Match with statement items available but none selected → FS-owned
  // confirmation first (BHV-033).
  const handleMatch = useCallback(() => {
    if (selectedStatementItems.length === 0 && statementItemCount > 0) {
      setMatchConfirmOpen(true)
      return
    }
    performMatch()
  }, [performMatch, selectedStatementItems.length, statementItemCount])

  const handleMatchConfirm = useCallback(
    (done: () => void) => {
      done()
      setMatchConfirmOpen(false)
      performMatch()
    },
    [performMatch]
  )

  const handleUnmatchConfirm = useCallback(async () => {
    if (!unmatchModal) return

    try {
      await unmatchCommands.unmatch(unmatchModal.id)
      setUnmatchModal(null)
    } catch {
      // Failure keeps the modal open — the feature already toasted (ADR-10).
    }
  }, [unmatchCommands, unmatchModal])

  const handleStatementItemCreated = useCallback((item: StatementItem) => {
    setStatementItemModalOpen(false)
    // Flag keyed by item id so "JUST CREATED" survives refetch (BHV-041).
    setJustCreatedItemIds((previous) => [...previous, item.id])
  }, [])

  const handleDuplicateStatementItem = useCallback(
    async (item: StatementItem) => {
      try {
        const copy = await duplicateCommands.duplicate(item)
        setJustCreatedItemIds((previous) => [...previous, copy.id])
      } catch {
        // The feature already toasted (ADR-10).
      }
    },
    [duplicateCommands]
  )

  const handleCcItemCreated = useCallback((item: CreditCardItem) => {
    setItemModal(null)
    // Created items surface with "JUST CREATED" on the match view (BHV-016).
    setJustCreatedItemIds((previous) => [...previous, item.id])
  }, [])

  // Inline save clears the suggested filter; the feature's invalidations
  // refetch both panes and variance recomputes (BHV-038).
  const handleInlineSaved = useCallback(() => {
    setFilterByCcItemId(null)
  }, [])

  const goToTab = useCallback(
    (nextTab: MatchPageTabEnum) => {
      history.push(
        url('ReconciliationMatchPage', {
          id: String(accountId),
          tab: nextTab,
        })
      )
    },
    [accountId, history, url]
  )

  // Submit → "New credit card bill" page (BHV-044).
  const handleSubmit = useCallback(() => {
    history.push(url('CreateCreditCardBillPage', { id: String(accountId) }))
  }, [accountId, history, url])

  return {
    state: {
      account,
      session,
      isLoadingAccount,
      activeTab: tab,
      selectedCcItems,
      selectedStatementItems,
      filterByCcItemId,
      justCreatedItemIds,
      itemModal,
      statementItemModalOpen,
      unmatchModal,
      auditLogItem,
      importModalOpen,
      matchConfirmOpen,
      unmatchedCount,
      matchedCount,
      reconcileItemContentTypeId: reconcileItemContentType?.id ?? null,
      footer: {
        hasSelection,
        selectedCcCount: selectedCcItems.length,
        selectedStatementCount: selectedStatementItems.length,
        selectionTotal,
        selectionVariance,
        isMatchDisabled,
        isMatching: matchStates.isMatching,
        endingBalance,
        clearedBalance: clearedBalance ?? 0,
        overallVariance,
        isSubmitDisabled,
      },
      isUnmatching: unmatchStates.isUnmatching,
    },
    methods: {
      setSelectedCcItems,
      setSelectedStatementItems,
      clearSelections,
      showSuggestedMatches: (item: CreditCardItem) =>
        setFilterByCcItemId(item.id),
      clearSuggestedFilter: () => setFilterByCcItemId(null),
      handleInlineSaved,
      openCreateItemModal: () => setItemModal({ mode: 'create' }),
      openEditItemModal: (item: CreditCardItem) =>
        // Matched items open read-only (BHV-019).
        setItemModal({ mode: item.isEditable ? 'edit' : 'view', item }),
      closeItemModal: () => setItemModal(null),
      handleCcItemCreated,
      openStatementItemModal: () => setStatementItemModalOpen(true),
      closeStatementItemModal: () => setStatementItemModalOpen(false),
      handleStatementItemCreated,
      handleDuplicateStatementItem,
      removeStatementItem: removeCommands.remove,
      archiveCreditCardItem: archiveCommands.archive,
      openUnmatchModal: setUnmatchModal,
      closeUnmatchModal: () => setUnmatchModal(null),
      handleUnmatchConfirm,
      openAuditLogModal: setAuditLogItem,
      closeAuditLogModal: () => setAuditLogItem(null),
      openImportModal: () => setImportModalOpen(true),
      closeImportModal: () => setImportModalOpen(false),
      handleMatch,
      handleMatchConfirm,
      dismissMatchConfirm: () => setMatchConfirmOpen(false),
      handleSessionChange: setSessionOverride,
      goToUnmatchedTab: () => goToTab(MatchPageTabs.UNMATCHED),
      goToMatchedTab: () => goToTab(MatchPageTabs.MATCHED),
      handleSubmit,
    },
  }
}
